import ClearIcon from '@material-ui/icons/Clear';
import { isEmpty, trim } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';

import {
  borderRadius,
  Col,
  Cols,
  fontSizingV2,
  spacingV2,
  Text,
  TextButton,
  TextInput,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { isValidEmail } from '~/src/dash/utils/email';

interface IRecipientTagProps {
  isDisabled?: boolean;
  recipient: string;
  onCancel?: (recipient: string) => void;
}

const RecipientTag = ({
  isDisabled = false,
  recipient = '',
  onCancel = () => null,
}: IRecipientTagProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div>
      <Cols
        smallHorizontalPaddingV2
        extraSmallVerticalPaddingV2
        style={{
          alignItems: 'center',
          backgroundColor: isHovered ? publicaColors.lightGray : publicaColors.veryLightGray,
          borderRadius: borderRadius.small,
          paddingRight: spacingV2.extraSmall, // this is to visually center Cols  since ClearIcon has extra width on the right
        }}
      >
        <Col style={{ marginRight: spacingV2.extraSmall }}>
          <Text extraSmall muted>
            {recipient}
          </Text>
        </Col>
        {!isDisabled && (
          <Col
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => onCancel(recipient)}
            style={{ paddingTop: spacingV2.extraSmall, cursor: 'pointer' }}
          >
            <ClearIcon
              style={{
                color: publicaColors.gray,
                height: fontSizingV2.small,
              }}
            />
          </Col>
        )}
      </Cols>
    </div>
  );
};

interface IRecipientsProps {
  isDisabled?: boolean;
  recipients: Array<string>;
  onChange: (recipients: Array<string>) => void;
}

export const Recipients = ({ isDisabled = false, onChange, recipients }: IRecipientsProps) => {
  // we need to use a refs instead of state for input and errorMessage in order for
  // the listener handler to always have access to the latest state of the component
  const input = useRef('');
  const errorMessageRef = useRef('');
  const [hasError, setHasError] = useState(false);

  // when user click "+ Add Recipients"
  const onAddRecipients = () => {
    // do nothing if it is diabled or if there is no input
    if (isDisabled || input.current === '') return false;
    // if (isDisabledRef.current || input.current === '') return false;

    // parse input
    const emails = input.current
      .split(',')
      .map(trim)
      .filter((v) => v !== '');

    // if there is any misformatted email, display error message and return
    if (emails.some((email) => !isValidEmail(email))) {
      errorMessageRef.current = 'One or more emails are invalid';
      setHasError(true);
      return;
    }

    // otherwise, add emails to recipients, remove duplicates, and reset error message
    onChange(Array.from(new Set([...recipients, ...emails])));
    if (errorMessageRef.current) errorMessageRef.current = '';
    setHasError(false);
    return;
  };

  // handler for when user presses key 'Enter'
  const onPressEnter = (e: KeyboardEvent) => {
    if (e.key === 'Enter') onAddRecipients();
  };

  // reset the listener whenever props are updated so that we do not have stale props
  useEffect(() => {
    document.removeEventListener('keydown', onPressEnter, false);
    if (!isDisabled) {
      document.addEventListener('keydown', onPressEnter, false);
    }

    return () => {
      document.removeEventListener('keydown', onPressEnter, false);
    };
  }, [isDisabled, recipients]);

  return (
    <Cols
      style={{
        pointerEvents: isDisabled ? 'none' : 'all',
        transition: 'color 0.5s ease-in-out',
      }}
    >
      <Col style={{ minWidth: '7rem' }}>
        <Text bold small muted={isDisabled}>
          Recipients
        </Text>
      </Col>
      <Col>
        <Cols wrap style={{ alignItems: 'center' }}>
          <Col style={{ margin: `0 ${spacingV2.medium} ${spacingV2.small} 0` }}>
            <TextInput
              enableClear
              width='20rem'
              initialValue={input.current}
              inputSize='small'
              isDisabled={isDisabled}
              handleOnChange={(v) => {
                input.current = v;
                if (v === '') {
                  setHasError(false);
                  errorMessageRef.current = '';
                }
              }}
              msDelay={0}
              placeholder='Enter email addresses, comma separated'
            />
          </Col>
          <Col marginBottom={spacingV2.small}>
            <TextButton
              size='small'
              disabled={isDisabled}
              color={publicaColors.blue}
              onClick={() => onAddRecipients()}
            >
              + Add Recipients
            </TextButton>
          </Col>
        </Cols>

        {hasError && (
          <Text small color={publicaColors.red}>
            {errorMessageRef.current}
          </Text>
        )}

        {!isEmpty(recipients) && (
          <Cols wrap smallVerticalPaddingV2 gap={spacingV2.small}>
            {recipients.map((recipient) => (
              <Col marginBottom={spacingV2.small}>
                <RecipientTag
                  isDisabled={isDisabled}
                  recipient={recipient}
                  onCancel={(recipient: string) =>
                    onChange(recipients.filter((v) => v !== recipient))
                  }
                />
              </Col>
            ))}
          </Cols>
        )}
      </Col>
    </Cols>
  );
};
